import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon, Monitor } from 'lucide-react';
import CTA from '../../components/CTA';

const LarneAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Larne | Port Worker Stress Relief</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Larne from Belfast clinic (25 miles). Shift work anxiety, port industry stress, sleep problems. Online sessions for irregular schedules. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Larne, shift worker sleep problems, port industry stress, hypnotherapy Larne anxiety, irregular schedule stress relief" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/larne-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Larne | Port Worker Stress Relief" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Larne. Shift work anxiety, port industry stress, sleep problems, online flexibility." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Larne",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Larne from Belfast clinic. Specializing in shift work anxiety, port industry stress, sleep problems from irregular schedules, and relationship strain from shift patterns.",
            "url": "https://www.sharonclare-hypnotherapy.com/larne-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Larne",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with shift work anxiety and sleep problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for shift work-related anxiety and sleep problems. Irregular hours disrupt your body's natural rhythms, making it hard to fall asleep, stay asleep, or feel rested. Solution-focused hypnotherapy helps re-establish healthier sleep patterns, reduces anxiety around shift changes, and teaches your nervous system to calm down even when your schedule is irregular. Many shift workers notice significant improvements in sleep quality within 4-6 sessions."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer online sessions for shift workers with irregular schedules?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. Online sessions via secure video call are ideal for Larne shift workers with irregular schedules. You can attend from home without adding travel time, schedule sessions around your shifts, and have the flexibility to book appointments that fit your rotating pattern. Online sessions provide the same professional anxiety treatment as in-person appointments - many shift workers find this the most practical option."
                }
              },
              {
                "@type": "Question",
                "name": "How far is Belfast from Larne for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 25 miles from Larne via the A8/M5, taking 25-30 minutes by car. This makes regular anxiety treatment appointments practical for in-person sessions. However, many Larne shift workers prefer online sessions for maximum flexibility, especially when working irregular hours makes scheduling challenging."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with port industry work stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for port industry work stress. Port work often involves physical demands, safety pressures, irregular hours, weather-dependent schedules, and the stress of managing unpredictable workloads. Hypnotherapy helps by reducing anxiety, building resilience, improving stress management, and helping you switch off from work pressures when you're at home, preventing work stress from affecting your relationships and quality of life."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on individual circumstances and goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and commitment to the process."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with relationship strain from shift work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, shift work often creates relationship strain - limited quality time with partners or family, fatigue-related irritability, and the challenge of being on different schedules. While hypnotherapy cannot change your work pattern, it can help by reducing anxiety and stress, improving sleep quality so you're less exhausted and irritable, building emotional resilience, and helping you be more present and patient during the time you do have with loved ones."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Larne
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy for shift workers and port industry professionals
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center justify-center space-x-3">
                  <Car className="w-6 h-6 text-accent-600" />
                  <p className="text-lg text-gray-700">
                    <span className="font-semibold">25 miles</span> from Larne via A8/M5 • <span className="font-semibold">25-30 min drive</span>
                  </p>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <Monitor className="w-6 h-6 text-accent-600" />
                  <p className="text-lg text-gray-700">
                    <span className="font-semibold">Online sessions ideal</span> for irregular shift schedules
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Larne */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Larne
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Larne and struggling with anxiety or stress, particularly related to shift work or port industry employment, you're not alone. As Northern Ireland's main ferry port with a population of around 19,000, Larne's economy is heavily influenced by the port and related industries. Many Larne residents work irregular hours in port operations, ferry services, logistics, security, or related shift-based roles that fundamentally disrupt normal sleep patterns and daily rhythms.
              </p>
              <p>
                Shift work creates unique psychological and physiological challenges that regular 9-to-5 workers don't face. Working nights, early mornings, rotating shifts, or irregular patterns disrupts your body's natural circadian rhythms, making it extremely difficult to fall asleep, stay asleep, or feel genuinely rested. This chronic sleep deprivation then triggers anxiety, irritability, difficulty concentrating, and a constant feeling of running on empty. Add to this the relationship strain from being on different schedules than partners or family, the challenge of maintaining social connections when you're working while others are free, and the stress of the work itself - and it's no wonder anxiety becomes overwhelming.
              </p>
              <p>
                While I don't have a physical clinic in Larne itself, I'm proud to serve the Larne community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast. The journey from Larne is approximately 25 miles via the A8 and M5, taking 25-30 minutes. For shift workers with irregular schedules, however, I particularly recommend online sessions via secure video call. Online sessions provide the same professional anxiety treatment from the comfort of your home, with the crucial advantage of maximum flexibility - you can schedule sessions around your shifts without adding travel time to an already exhausting schedule.
              </p>
              <p>
                As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand shift work intimately - healthcare runs 24/7 and I've experienced firsthand the toll that irregular hours can take on sleep, mood, relationships, and wellbeing. I specialize in helping shift workers manage anxiety, restore healthier sleep patterns despite irregular schedules, reduce work-related stress, and build resilience to handle the unique pressures of port and shift-based employment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects Shift Workers */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Shift Work Anxiety Affects Larne Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Shift Work Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Chronic sleep problems and insomnia',
                    'Fatigue-related anxiety and irritability',
                    'Difficulty switching off between shifts',
                    'Anticipatory anxiety before shift changes',
                    'Physical exhaustion but mind racing',
                    'Constant feeling of being "wired but tired"',
                    'Difficulty concentrating or making decisions',
                    'Mood swings and emotional exhaustion'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Port Industry Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'Rotating shift patterns disrupting sleep',
                    'Night shift fatigue and health concerns',
                    'Safety pressures in port operations',
                    'Weather-dependent unpredictable schedules',
                    'Relationship strain from irregular hours',
                    'Missing family time and social isolation',
                    'Physical demands combined with fatigue',
                    'Financial pressure to work overtime despite exhaustion'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                If shift work anxiety is affecting your sleep, relationships, health, or quality of life, you don't have to accept it as inevitable. Solution-focused hypnotherapy offers an evidence-based approach that addresses shift work-specific challenges, helping you restore healthier sleep patterns, reduce anxiety, and build resilience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps Shift Workers
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy is particularly effective for shift work anxiety because it works directly with your nervous system to calm stress responses, restore healthier sleep patterns, and build resilience despite irregular schedules.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Stress Response',
                  text: 'Soothe the overactive fight-or-flight response that shift work triggers. Your nervous system learns to calm down even when your schedule is irregular and unpredictable.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Build Shift Work Resilience',
                  text: 'Develop better stress management and emotional resilience specific to shift work challenges. Learn to handle fatigue, schedule changes, and work pressures without overwhelming anxiety.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Sleep Quality',
                  text: 'Re-establish healthier sleep patterns despite irregular hours. Learn techniques to fall asleep when you need to, regardless of what time your body thinks it is.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Shift work sleep disorder and insomnia',
                    'Fatigue-related anxiety and irritability',
                    'Work stress and safety concerns',
                    'Anticipatory anxiety about shift changes'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Relationship strain from irregular hours',
                    'Social isolation and missing family time',
                    'Difficulty switching off between shifts',
                    'Burnout and emotional exhaustion'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Choose Sharon Clare Hypnotherapy for Larne?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Shift Work from Experience
                </h3>
                <p className="text-gray-700">
                  As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand shift work intimately. Healthcare runs 24/7 and I've experienced firsthand the toll that irregular hours can take on sleep, mood, relationships, and wellbeing. I specialize in helping shift workers manage the unique challenges of irregular schedules.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Flexible Online Sessions Ideal for Shift Workers
                </h3>
                <p className="text-gray-700">
                  Online sessions via secure video call are perfect for Larne shift workers with irregular schedules. Attend from home without travel time, schedule around your shifts, and have the flexibility to book appointments that fit your rotating pattern. Online sessions provide the same professional treatment - many shift workers find this the most practical option.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Practical Distance from Larne
                </h3>
                <p className="text-gray-700">
                  At 25 miles via the A8/M5 (25-30 minutes drive), my Belfast clinic is accessible for in-person sessions if you prefer face-to-face treatment. However, for maximum flexibility, particularly when working irregular shifts, many Larne clients choose online sessions to avoid adding travel time to an already demanding schedule.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Treatment for Sleep Problems
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is highly effective for shift work sleep disorder and related anxiety. By working with your nervous system to restore healthier sleep patterns, reduce stress responses, and build resilience, hypnotherapy addresses the root causes rather than just managing symptoms. Most clients notice significant improvements in sleep quality within 4-6 sessions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting to Belfast / Online Sessions */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Treatment Options for Larne Shift Workers
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Monitor className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions (Recommended for Shift Workers)</h3>
                  <p className="text-gray-700 mb-4">
                    For maximum flexibility with irregular shift patterns, online sessions via secure video call are ideal:
                  </p>
                  <ul className="space-y-2">
                    {[
                      'No travel time - attend from home in Larne',
                      'Schedule around your shifts easily',
                      'Same professional treatment as in-person',
                      'Increased privacy and comfort',
                      'Perfect for rotating or unpredictable schedules'
                    ].map((item) => (
                      <li key={item} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-gray-200">
              <div className="flex items-start space-x-4">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">In-Person Sessions at Belfast Clinic</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the A8 toward Belfast, then M5
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> 25 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 25-30 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Parking:</span> Free street parking available
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Larne Shift Workers Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I work rotating shifts at the port and my sleep was completely destroyed. I'd lie awake for hours after night shifts, then be exhausted during the day. The anxiety was constant. Sharon's hypnotherapy helped me re-establish healthier sleep patterns and manage the stress. I still work the same shifts but I'm sleeping so much better and the anxiety has massively reduced. The online sessions were perfect for my irregular schedule."
                </p>
                <p className="font-semibold text-gray-900">D.P., Larne - Shift Work Sleep Disorder and Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working nights at the ferry port, I was constantly stressed, exhausted, and snapping at my family even though I didn't want to. Sharon understood shift work from her own nursing experience. The hypnotherapy helped me manage the fatigue-related irritability and build resilience. I'm much calmer now, sleeping better between shifts, and my relationship with my partner has improved because I'm not so worn down and anxious all the time."
                </p>
                <p className="font-semibold text-gray-900">K.T., Larne - Fatigue Anxiety and Relationship Strain</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I dreaded every shift change - the anticipatory anxiety would start days before. My mind would race, I couldn't relax, and the sleep disruption made everything worse. After hypnotherapy with Sharon, I handle shift changes so much better. The anxiety is manageable, I fall asleep more easily regardless of the time, and I feel like I have some control back. Using online sessions from home in Larne was ideal - no extra travel stress."
                </p>
                <p className="font-semibold text-gray-900">M.L., Larne - Shift Change Anxiety and Sleep Problems</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Larne Shift Worker Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with shift work anxiety and sleep problems?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for shift work-related anxiety and sleep problems. Irregular hours disrupt your body's natural rhythms, making it hard to fall asleep, stay asleep, or feel rested. Solution-focused hypnotherapy helps re-establish healthier sleep patterns, reduces anxiety around shift changes, and teaches your nervous system to calm down even when your schedule is irregular. Many shift workers notice significant improvements in sleep quality within 4-6 sessions.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online sessions for shift workers with irregular schedules?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Online sessions via secure video call are ideal for Larne shift workers with irregular schedules. You can attend from home without adding travel time, schedule sessions around your shifts, and have the flexibility to book appointments that fit your rotating pattern. Online sessions provide the same professional anxiety treatment as in-person appointments - many shift workers find this the most practical option.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Larne for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 25 miles from Larne via the A8/M5, taking 25-30 minutes by car. This makes regular anxiety treatment appointments practical for in-person sessions. However, many Larne shift workers prefer online sessions for maximum flexibility, especially when working irregular hours makes scheduling challenging.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with port industry work stress?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is very effective for port industry work stress. Port work often involves physical demands, safety pressures, irregular hours, weather-dependent schedules, and the stress of managing unpredictable workloads. Hypnotherapy helps by reducing anxiety, building resilience, improving stress management, and helping you switch off from work pressures when you're at home, preventing work stress from affecting your relationships and quality of life.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for anxiety?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on individual circumstances and goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and commitment to the process.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with relationship strain from shift work?
                </h3>
                <p className="text-gray-700">
                  Yes, shift work often creates relationship strain - limited quality time with partners or family, fatigue-related irritability, and the challenge of being on different schedules. While hypnotherapy cannot change your work pattern, it can help by reducing anxiety and stress, improving sleep quality so you're less exhausted and irritable, building emotional resilience, and helping you be more present and patient during the time you do have with loved ones.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Better Sleep and Less Anxiety"
        subtitle="Take the first step toward managing shift work stress, sleeping better, and feeling calmer. Book a free consultation today and discover how hypnotherapy can help you thrive despite irregular schedules."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default LarneAnxietyStress;
